import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, AlertTriangle } from "lucide-react";

const FACTS = [
  "Calibrating probe trajectory through folded space…",
  "Over 70% of catalogued worlds show rain made of something other than water.",
  "Biosignature scanners listen for rhythm before they listen for chemistry.",
  "Triangulating sector coordinates from three dead pulsars.",
  "Some atmospheres sing when a probe breaks their upper layers.",
  "Mythic-class worlds are logged roughly once in every twenty-five probes.",
  "Decoding spectral lines: looking for the color of this world's sky.",
  "Explorers report that gravity below 0.5 g makes every landing feel like a dance.",
  "ARCHIVE cross-references every new name against 4,000 years of stories.",
  "Night-side landings are safer. Day-side landings are more fun.",
  "Hazard ratings above 8 come with a complimentary farewell letter template.",
  "Rendering orbital visual: compositing cloud decks and terminator shadows…",
  "A planet's lore is written in the tone the probe hears first.",
  "Leave a beacon. Someone will follow your notes.",
];

const STAGES = {
  queued: { label: "Launching probe", pct: 8 },
  scanning: { label: "Scanning sector · decoding biosignatures", pct: 45 },
  rendering: { label: "Rendering orbital visual", pct: 82 },
  complete: { label: "Lock acquired", pct: 100 },
  failed: { label: "Probe lost", pct: 100 },
};

export const ProbeSequence = ({ job, onDismiss, onRetry }) => {
  const [fact, setFact] = useState(0);
  const stage = STAGES[job?.status] || STAGES.queued;

  useEffect(() => {
    const t = setInterval(() => setFact((f) => (f + 1) % FACTS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <AnimatePresence>
      {job && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-[#05070D]/92 backdrop-blur-md p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="probe-sequence-overlay">
          <div className="w-full max-w-3xl grid md:grid-cols-[320px_1fr] gap-10 items-center">
            <div className="relative mx-auto h-72 w-72 md:h-80 md:w-80" data-testid="probe-radar">
              {[1, 0.75, 0.5, 0.25].map((s) => (
                <div key={s} className="absolute rounded-full border border-cyan-400/25"
                  style={{ inset: `${(1 - s) * 50}%` }} />
              ))}
              <div className="absolute inset-0 rounded-full border border-cyan-400/40 animate-pulse-ring" />
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-cyan-400/15" />
              <div className="absolute top-1/2 left-0 right-0 h-px bg-cyan-400/15" />
              {job.status !== "complete" && job.status !== "failed" && (
                <div className="absolute inset-0 rounded-full animate-sweep"
                  style={{ background: "conic-gradient(from 0deg, rgba(0,240,255,0.55), rgba(0,240,255,0.08) 60deg, transparent 90deg)" }} />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                {job.status === "failed" ? (
                  <AlertTriangle className="h-10 w-10 text-[#FF5500]" />
                ) : (
                  <motion.div animate={job.status === "complete" ? { scale: [1, 1.4, 1] } : { scale: 1 }} transition={{ duration: 0.8 }}>
                    <Radar className="h-10 w-10 text-cyan-300" />
                  </motion.div>
                )}
              </div>
              {[[22, 30], [68, 24], [40, 70], [76, 62]].map(([x, y], i) => (
                <span key={i} className="absolute h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse"
                  style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.4}s` }} />
              ))}
            </div>

            <div className="text-left">
              <p className="label-mono mb-3">Probe {job.id?.slice(0, 8)}</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-slate-50 text-glow" data-testid="probe-stage-label">
                {stage.label}
              </h2>
              <div className="mt-6 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full rounded-full"
                  style={{ background: job.status === "failed" ? "#FF5500" : "linear-gradient(90deg,#00F0FF,#8A2BE2)" }}
                  animate={{ width: `${stage.pct}%` }} transition={{ duration: 1.2, ease: "easeOut" }} />
              </div>
              <div className="mt-8 min-h-[4.5rem]">
                {job.status === "failed" ? (
                  <p className="text-sm text-slate-300" data-testid="probe-error">{job.error || "The probe stopped transmitting. Try again."}</p>
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.p key={fact} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.4 }} className="text-base text-slate-300 leading-relaxed" data-testid="probe-teaser-fact">
                      {FACTS[fact]}
                    </motion.p>
                  </AnimatePresence>
                )}
              </div>
              <div className="mt-6 flex gap-3">
                {job.status === "failed" ? (
                  <>
                    <button className="btn-primary" onClick={onRetry} data-testid="probe-retry-button">Launch another probe</button>
                    <button className="btn-ghost" onClick={onDismiss} data-testid="probe-dismiss-button">Close</button>
                  </>
                ) : job.status !== "complete" ? (
                  <button className="btn-ghost" onClick={onDismiss} data-testid="probe-background-button">Continue in background</button>
                ) : null}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
